import { Button } from "antd";
import { useEffect } from "react";
import { BoldOutlined } from "@ant-design/icons";
import CharacterCount from "@tiptap/extension-character-count";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import "./TiptapEditor.scss";

type TiptapEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  canEdit: boolean;
};

const TiptapEditor = ({ value = "", onChange, canEdit }: TiptapEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      CharacterCount.configure({
        limit: 10000, // 可選，設定上限（可不設）
      }),
      Placeholder.configure({
        placeholder:
          "One ____, please.\nOne ticket, please.\n\nI'd like to ____ a ____.\nI'd like to buy a ticket.",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    content: value,
    editable: canEdit,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      if (html !== value) {
        onChange?.(html);
      }
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "<p></p>", false);
    }
    editor?.setEditable(canEdit);
  }, [value, editor, canEdit]);

  if (!editor) return null;

  return (
    <div className="tiptap-wrapper">
      {/* 自定 toolbar：傳 editor 實例下去 */}
      <CustomToolbar editor={editor} canEdit={canEdit} />

      {/* 編輯區 */}
      <div className={`tiptap-editor ${!canEdit ? "tiptap-readonly" : ""}`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TiptapEditor;

const CustomToolbar = ({
  editor,
  canEdit,
}: {
  editor: Editor;
  canEdit: boolean;
}) => {
  if (!editor) return null;

  return (
    <div className={`w-full toolbar ${!canEdit ? "readonly" : ""}`}>
      <Button
        type={editor.isActive("bold") ? "primary" : "text"}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={<BoldOutlined />}
      />
    </div>
  );
};
